import { type ProductPriceInterval } from "api-client";
import { Icon } from "icons";
import { useTranslation } from "react-i18next";
import { cn } from "ui";
import { getContrastTextColor } from "utils";

import { PublicProfileContactForm } from "src/components/PublicProfileContactForm";
import { PublicProfileLinkCard } from "src/components/PublicProfileLinkCard";
import { PublicProfileProductCard } from "src/components/PublicProfileProductCard";
import { PublicProfileSocialLinks } from "src/components/PublicProfileSocialLinks";
import { SztamaLogo } from "src/components/SztamaLogo";
import { type ProfileContactFormQuestionnaire } from "src/profile/profileContactForm";
import {
  getProfileWallpaperBackground,
  getProfileWallpaperBlurBackground,
  type ProfileTheme,
} from "src/profile/profileDesign";

export interface PublicProfilePageResource {
  id: string;
  type: "LINK" | "FILE";
  title: string | null;
  description: string | null;
  url: string | null;
  fileId: string | null;
  fileName: string | null;
  fileUrl: string | null;
}

export interface PublicProfilePageLink {
  id: string;
  placement: "HEADER" | "LINKS";
  titleOverride: string | null;
  platform: string | null;
  resourceId: string;
  resource: PublicProfilePageResource;
  thumbnailFileId: string | null;
  thumbnailUrl: string | null;
  thumbnailIcon: string | null;
  isVisible: boolean;
}

export interface PublicProfilePageProduct {
  id: string;
  slug: string;
  name: string;
  imageUrl: string | null;
  prices: {
    id: string;
    unitAmount: number;
    currency: string;
    interval: ProductPriceInterval | null;
    intervalCount: number;
  }[];
}

export interface PublicProfilePageData {
  id: string;
  slug: string;
  displayName: string;
  bio: string;
  socialLinksPosition: "TOP" | "BOTTOM";
  avatarUrl: string | null;
  theme: ProfileTheme;
  links: PublicProfilePageLink[];
  products: PublicProfilePageProduct[];
  contactForm?: {
    buttonText: string;
    questionnaire: ProfileContactFormQuestionnaire | null;
  } | null;
}

interface PublicProfilePageProps {
  profile: PublicProfilePageData;
  variant?: "page" | "preview";
}

export function PublicProfilePage(props: PublicProfilePageProps) {
  const translation = useTranslation();
  const t = translation.t;
  const profile = props.profile;
  const profileTitle = getPublicProfileTitle(profile.displayName, profile.slug);
  const theme = profile.theme;
  const contrastColor = getContrastTextColor(theme.backgroundColor);
  const isPreview = props.variant === "preview";

  const socialLinks = profile.links.filter((link) => {
    return link.placement === "HEADER" && link.isVisible;
  });
  const contentLinks = profile.links.filter((link) => {
    return link.placement === "LINKS" && link.isVisible;
  });

  return (
    <div
      className={cn(
        "relative overflow-x-hidden",
        isPreview ? "h-full min-h-full overflow-y-auto" : "min-h-screen",
      )}
      style={{
        background: getProfileWallpaperBackground(
          theme.backgroundColor,
          theme.wallpaperStyle,
        ),
      }}
    >
      {theme.wallpaperStyle === "BLUR" ? (
        <div
          className="pointer-events-none absolute inset-[-16%] blur-3xl"
          style={{
            background: getProfileWallpaperBlurBackground(
              theme.backgroundColor,
            ),
          }}
        />
      ) : null}
      <a
        href="https://sztama.app"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Sztama"
        className={cn(
          "absolute z-10 opacity-70 transition-opacity hover:opacity-100",
          isPreview ? "left-4 top-5" : "left-5 top-5 sm:left-8 sm:top-8",
        )}
        style={{ color: contrastColor }}
      >
        <SztamaLogo
          className={isPreview ? "h-2.5 w-[91px]" : "h-3 w-[109px]"}
        />
      </a>
      <div
        className={cn(
          "relative mx-auto max-w-md px-5 pt-24",
          profile.contactForm ? "pb-32" : "pb-16",
        )}
      >
        <div className="flex flex-col items-center text-center">
          {profile.avatarUrl ? (
            <img
              src={profile.avatarUrl}
              alt={profileTitle}
              className="size-22 rounded-full object-cover"
            />
          ) : (
            <div className="flex size-22 items-center justify-center rounded-full bg-white/70 text-black/35">
              <Icon name="tabler-user" className="size-8" />
            </div>
          )}

          <h1
            className="mt-5 text-[22px] font-bold tracking-tight"
            style={{ color: theme.titleTextColor }}
          >
            {profileTitle}
          </h1>

          {profile.bio && (
            <p
              className="mt-3 text-sm leading-relaxed max-w-xs"
              style={{ color: theme.bioTextColor }}
            >
              {profile.bio}
            </p>
          )}

          {profile.socialLinksPosition === "TOP" ? (
            <PublicProfileSocialLinks
              links={socialLinks}
              color={contrastColor}
              className="mt-6"
            />
          ) : null}
        </div>

        {contentLinks.length > 0 && (
          <div className="mt-10 flex flex-col gap-3">
            {contentLinks.map((link) => {
              return (
                <PublicProfileLinkCard
                  key={link.id}
                  link={link}
                  theme={theme}
                />
              );
            })}
          </div>
        )}

        {profile.products.length > 0 && (
          <div className="mt-10 flex flex-col gap-3">
            {profile.products.map((product) => {
              return (
                <PublicProfileProductCard
                  key={product.id}
                  coachSlug={profile.slug}
                  product={product}
                  theme={theme}
                />
              );
            })}
          </div>
        )}

        {profile.socialLinksPosition === "BOTTOM" ? (
          <PublicProfileSocialLinks
            links={socialLinks}
            color={contrastColor}
            className={
              contentLinks.length > 0 || profile.products.length > 0
                ? "mt-8"
                : "mt-10"
            }
          />
        ) : null}

        <footer className="mt-14 flex flex-col items-center gap-3">
          <a
            href="https://sztama.app"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs font-medium opacity-70 transition-opacity hover:opacity-100"
            style={{ color: contrastColor }}
          >
            <span>{t("profileCoachCta")}</span>
            <Icon name="tabler-arrow-right" className="size-3.5" />
          </a>
          <div className="flex items-center gap-3">
            <a
              href="https://sztama.app/privacy"
              className="text-[11px] tracking-wide opacity-60 transition-opacity hover:opacity-80"
              style={{ color: contrastColor }}
            >
              {t("profilePrivacy")}
            </a>
          </div>
        </footer>
      </div>
      {profile.contactForm ? (
        <PublicProfileContactForm
          buttonText={profile.contactForm.buttonText}
          buttonColor={theme.buttonColor}
          buttonTextColor={theme.buttonTextColor}
          questionnaire={profile.contactForm.questionnaire}
          slug={profile.slug}
          isPreview={isPreview}
        />
      ) : null}
    </div>
  );
}

function getPublicProfileTitle(
  displayName: string | null | undefined,
  slug: string,
): string {
  if (displayName) {
    return displayName;
  }

  return `@${slug}`;
}
